import { Box, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalBase } from '../components/ModalBase';
import { getRunData, getRunResult, setRunData, getIsShowRunTrainedModelModal, getRunSchemaOrdering, getRunSchemaOrderingResult, setRunSchemaOrdering, setRunSchemaOrderingResult, getOpenedModel, getIsLoadingResults } from '../reducers/trainedModel/trainedModelReducer';
import { closeRunModelModalAction, runModelModalAction } from '../sagas/trainedModelSaga/trainedModelSagaActions';
import CodeMirror from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { CodeMirrorStyles, CodeMirrorTheme, FlexBoxInline, InputBaseSx, PrimaryBtnBorderlessSx, SecondaryBtnSx } from '../@shared/templates';
import { Palette, Sizes } from '../@shared/palette';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { StructureColumnTypeEnum, StructureItemType } from '../@shared/Models/Models';
import { ContextHelper } from '../components/ContextHelper';
import { NotificationModel, NotificationModelType } from '../reducers/main/mainModel';
import { dataToJson, dataToCsv, SchemaDataRow, parseFileData } from '../@shared/schemaDataParser';
import { onThrowAlert } from '../sagas/dataSaga/dataSagaActions';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Download from '@mui/icons-material/Download';
import { filterHierarchy, getSchemaNamesDictionary } from '../@shared/schemaUtils';


export const withRunModel = <T extends Record<string, unknown>>(
  Component: React.ComponentType<T>
): React.FC<T> => (props) => {
  const dispatch = useDispatch();
  const handleClose = () => dispatch(closeRunModelModalAction())
  const onSave = () => dispatch(runModelModalAction())
  const data = useSelector(getRunData);
  const schemaOrdering = useSelector(getRunSchemaOrdering);
  const schemaOrderingResult = useSelector(getRunSchemaOrderingResult);
  const model = useSelector(getOpenedModel);
  const result = useSelector(getRunResult);
  const isOpen = useSelector(getIsShowRunTrainedModelModal);
  const isLoading = useSelector(getIsLoadingResults);
  const flattenedSchemaItems = React.useMemo(() => {
    if (!model?.schema?.schemaItems) return [];
    const filtered = filterHierarchy(model.schema.schemaItems, [StructureColumnTypeEnum.Feature, StructureColumnTypeEnum.Info]);
    return getSchemaNamesDictionary(filtered)
      .slice()
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [model?.schema]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const onChangeData = React.useCallback((value: string) => {
    dispatch(setRunData(value))
  }, [dispatch]);

  const baseOrder = React.useMemo(() => {
    return flattenedSchemaItems
      .filter(x => x.schemaItem.typeId !== StructureColumnTypeEnum.Target)
      .map(x => x.key)
      .join(",");
  }, [flattenedSchemaItems])

  const baseResultOrder = React.useMemo(() => {
    return flattenedSchemaItems
      .filter(x => x.schemaItem.typeId === StructureColumnTypeEnum.Target)
      .map(x => x.key)
      .join(",");
  }, [flattenedSchemaItems]);

  const handleLoadFile = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    const schemaItems = flattenedSchemaItems
      .filter(x => x.schemaItem.typeId !== StructureColumnTypeEnum.Target)
      .map(f => ({ ...f.schemaItem, name: f.key }));
    reader.onload = (e) => {
      const fileContent = e.target?.result as string;
      if (fileContent && schemaItems.length > 0) {
        const result = parseFileData(fileContent, schemaItems);

        if (result.isValid) {
          dispatch(setRunData(dataToJson(result.data, true)));
          dispatch(setRunSchemaOrdering(result.schemaOrdering.join(",")));
        } else {
          dispatch(onThrowAlert({
            message: result.error || "Failed to parse CSV",
            lifeTime: 15000,
            type: NotificationModelType.Error
          } as NotificationModel));
        }
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  }, [dispatch, flattenedSchemaItems]);

  const onCopyBaseOrder = React.useCallback(async () => {
    navigator.clipboard.writeText(baseOrder);
  }, [baseOrder]);

  const onCopyBaseOrderResult = React.useCallback(async () => {
    navigator.clipboard.writeText(baseResultOrder);
  }, [baseResultOrder]);

  const handleDownloadCsv = React.useCallback(() => {
    try {
      if (!result || !Array.isArray(result.data) || result.data.length === 0) {
        dispatch(onThrowAlert({
          message: "No results to download",
          lifeTime: 5000,
          type: NotificationModelType.Warning
        } as NotificationModel));
        return;
      }

      const flattenedMap = new Map(flattenedSchemaItems.map(f => [f.key, f.schemaItem]));

      const resultSchemaItems = result.schemaOrdering.map((x: string) => {
        const item = flattenedMap.get(x);
        if (!item) {
          if (x !== "PredictedLabel") {
            throw new Error(`Schema item not found for name: ${x}`);
          }
          return { name: "PredictedLabel", typeId: StructureColumnTypeEnum.Target, valueTypeId: StructureItemType.Number };
        }

        return { ...item, name: x };
      });

      if (resultSchemaItems.length === 0) {
        dispatch(onThrowAlert({
          message: "No target schema items found",
          lifeTime: 5000,
          type: NotificationModelType.Error
        } as NotificationModel));
        return;
      }

      const csvContent = dataToCsv(result.data as SchemaDataRow[], resultSchemaItems);

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `${model?.name || 'model'}_results_${new Date().getTime()}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      dispatch(onThrowAlert({
        message: `Failed to download CSV: ${(error as Error).message}`,
        lifeTime: 10000,
        type: NotificationModelType.Error
      } as NotificationModel));
    }
  }, [result, flattenedSchemaItems, model?.name, dispatch]);

  return <>
    <ModalBase
      isOpen={isOpen}
      onCancel={handleClose}
      onSave={onSave}
      title="Run"
      isProcessing={isLoading}
      primaryBtnText='Run'
    >
      <Box sx={{ paddingTop: "0px", paddingBottom: "15px" }}>
        <ContextHelper boxSx={{ marginBottom: "5px" }} content={<>The list of field names that define the order of values for each input row in data. Each value in a row must match the position of the corresponding field name.</>}>
          <TextField sx={{ ...InputBaseSx }} value={schemaOrdering} onChange={(e) => dispatch(setRunSchemaOrdering(e.target.value))} fullWidth className='' id="Schema-Ordering-field" size="small" label="Schema Ordering" variant="outlined" />
        </ContextHelper>
        <Box sx={{ ...FlexBoxInline, marginBottom: "10px" }}>
          <Typography sx={{
            color: Palette.textColorSecondary,
            fontSize: Sizes.textSecondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>
            Basic order: {baseOrder}
          </Typography>
          <Tooltip title={"Copy"} placement="bottom" arrow>
            <IconButton sx={{ ...SecondaryBtnSx, p: "5px", border: "none" }} aria-label="copy" onClick={onCopyBaseOrder}>
              <ContentCopyIcon sx={{ fontSize: Sizes.textPrimary }} />
            </IconButton>
          </Tooltip>
        </Box>
        <ContextHelper boxSx={{ marginBottom: "5px" }} content={<>The list of field names that define the order of values in each prediction result row returned by the model.</>}>
          <TextField sx={{ ...InputBaseSx, marginBottom: "5px" }} value={schemaOrderingResult} onChange={(e) => dispatch(setRunSchemaOrderingResult(e.target.value))} fullWidth className='' id="Schema-Ordering-Result-field" size="small" label="Schema Ordering Result" variant="outlined" />
        </ContextHelper>
        <Box sx={{ ...FlexBoxInline, marginBottom: "10px" }}>
          <Typography sx={{
            color: Palette.textColorSecondary,
            fontSize: Sizes.textSecondary,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>
            Basic order result: {baseResultOrder}
          </Typography>
          <Tooltip title={"Copy"} placement="bottom" arrow>
            <IconButton sx={{ ...SecondaryBtnSx, p: "5px", border: "none" }} aria-label="copy" onClick={onCopyBaseOrderResult}>
              <ContentCopyIcon sx={{ fontSize: Sizes.textPrimary }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ ...FlexBoxInline }}>
          <Box sx={{ width: "50%" }}>
            <Box sx={{ ...FlexBoxInline, alignItems: "flex-end", justifyContent: "space-between", marginBottom: "0px" }}>
              <ContextHelper content={<>Provide input values for model prediction as a JSON array of rows. Each row must follow the order defined in Schema Ordering, where the first value corresponds to the first field name, the second to the second field name, and so on.</>} boxSx={{ marginBottom: "5px" }}>
                <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Data:</Typography>
              </ContextHelper>
              <Tooltip title={"Upload CSV or JSON file"} placement="bottom" arrow>
                <IconButton sx={{ ...PrimaryBtnBorderlessSx, fontSize: Sizes.icon, p: "5px", border: "none" }} size="medium" onClick={handleLoadFile}>
                  <UploadFileIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
            <CodeMirror value={data} height="400px" extensions={[json()]} onChange={onChangeData} style={CodeMirrorStyles} theme={CodeMirrorTheme} />
          </Box>
          <Box sx={{ width: "50%", position: "relative" }}>
            <Box sx={{ ...FlexBoxInline, alignItems: "flex-end", justifyContent: "space-between", marginBottom: "0px" }}>
              <ContextHelper content={<>The model output returned after running predictions. Each row in the result corresponds to a processed input row, and the values follow the order defined in resultSchemaOrdering.</>} boxSx={{ marginBottom: "5px" }}>
                <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Result:</Typography>
              </ContextHelper>
              <Tooltip title={"Download CSV"} placement="bottom" arrow>
                <IconButton disabled={!result || !Array.isArray(result.data) || result.data.length === 0} sx={{ ...PrimaryBtnBorderlessSx, fontSize: Sizes.icon, p: "5px", border: "none" }} size="medium" onClick={handleDownloadCsv}>
                  <Download fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ position: "relative" }}>
              <CodeMirror readOnly value={JSON.stringify(result, null, 2)} height="400px" extensions={[json()]} onChange={onChangeData} style={CodeMirrorStyles} theme={CodeMirrorTheme} />
              {isLoading && <Box sx={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0, background: Palette.btnPrimaryHover }}>
                <CircularProgress sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                  color: Palette.btnPrimary
                }} />
              </Box>}
            </Box>
          </Box>
        </Box>
      </Box>
      <input
        type="file"
        ref={fileInputRef}
        accept=".csv,text/csv,.json,application/json"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        aria-label="Upload CSV or JSON file"
      />
    </ModalBase>
    <Component {...props as T} />
  </>
}